import React, { useEffect } from "react";
import { useAppDispatch } from "src/redux/hooks";
import { cgmManager } from "src/services/cgm/cgmManager";
import { parseCgmMessage, handleCgmMessage } from "src/services/cgm";
import { STORAGE_KEYS } from "src/constants/common";
import storage from "src/helpers/storage";
import { warmupStarted } from "src/redux/slices/cgmSlice";

/**
 * Global listener for CGM postMessages from RN.
 * Updates Redux so insights, device card, and connection journey stay in sync.
 * Restores warmup from storage on app load so ConnectedBottomSheet shows
 * correct status on first open (before user visits CGM journey).
 */
const CgmMessageListener: React.FC = () => {
  const dispatch = useAppDispatch();

  useEffect(() => {
    cgmManager.init(dispatch);
  }, [dispatch]);

  // Restore warmup from storage on app load — ensures ConnectedBottomSheet
  // shows "Warmup" on first open, not "Connected" from RN's "bound" response
  useEffect(() => {
    const storedWarmup = storage.getItem(STORAGE_KEYS.CGM_WARMUP_END_DATE);
    if (storedWarmup) {
      const endTime = new Date(storedWarmup).getTime();
      if (!Number.isNaN(endTime) && endTime > Date.now()) {
        dispatch(warmupStarted(storedWarmup));
      } else {
        storage.removeItem(STORAGE_KEYS.CGM_WARMUP_END_DATE);
      }
    }
  }, [dispatch]);

  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      const payload = parseCgmMessage(event);
      handleCgmMessage(payload, dispatch);
    };

    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [dispatch]);

  return null;
};

export default CgmMessageListener;
